import React, { useState } from "react"
import PropTypes from "prop-types"
import { Spinner, Modal, ModalHeader, ModalBody, Button } from "reactstrap"
import { getPdfHtml, descargarExcel } from "./api"

/**
 * Botones para exportar PDF (preview en modal) y Excel (descarga directa).
 * Se usa tanto a nivel global (sin sucursal) como por sucursal.
 */
const ExportarBoton = ({ exportOpts, sucursal, size = "sm", labelPDF = "PDF", labelExcel = "Excel", iconOnly = false }) => {
  const [exportando, setExportando] = useState(null) // "pdf" | "excel" | null
  const [pdfModal, setPdfModal] = useState(false)
  const [pdfHtml, setPdfHtml] = useState("")
  const [error, setError] = useState("")

  const handlePDF = async () => {
    setExportando("pdf")
    setError("")
    try {
      const html = await getPdfHtml({ ...exportOpts, sucursal })
      setPdfHtml(html)
      setPdfModal(true)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar el PDF.")
    } finally {
      setExportando(null)
    }
  }

  const handleExcel = async () => {
    setExportando("excel")
    setError("")
    try {
      await descargarExcel({ ...exportOpts, sucursal })
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo descargar el Excel.")
    } finally {
      setExportando(null)
    }
  }

  const btnClass = `btn btn-${size}`

  return (
    <>
      <div className="d-flex align-items-center gap-1">
        <button
          type="button"
          className={`${btnClass} btn-outline-success`}
          onClick={handleExcel}
          disabled={exportando !== null}
          title={iconOnly ? `Exportar ${sucursal || "todo"} a Excel` : ""}
        >
          {exportando === "excel" ? (
            <Spinner size="sm" />
          ) : (
            <>
              <i className={`mdi mdi-file-excel ${iconOnly ? "" : "me-1"}`} />
              {!iconOnly && labelExcel}
            </>
          )}
        </button>
        <button
          type="button"
          className={`${btnClass} btn-outline-danger`}
          onClick={handlePDF}
          disabled={exportando !== null}
          title={iconOnly ? `Vista previa PDF ${sucursal || ""}` : ""}
        >
          {exportando === "pdf" ? (
            <Spinner size="sm" />
          ) : (
            <>
              <i className={`mdi mdi-file-pdf ${iconOnly ? "" : "me-1"}`} />
              {!iconOnly && labelPDF}
            </>
          )}
        </button>
      </div>

      {error && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            padding: "10px 14px",
            borderRadius: 8,
            zIndex: 2000,
            maxWidth: 400,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <i className="mdi mdi-alert-circle me-2" />
          {error}
          <button
            type="button"
            className="btn-close ms-2"
            style={{ fontSize: 10 }}
            onClick={() => setError("")}
          />
        </div>
      )}

      <Modal isOpen={pdfModal} toggle={() => setPdfModal(false)} size="xl" centered>
        <ModalHeader toggle={() => setPdfModal(false)}>
          Vista previa PDF — {sucursal || "Todas las sucursales"}
        </ModalHeader>
        <ModalBody style={{ padding: 0, height: "75vh" }}>
          <iframe
            title="PDF preview"
            srcDoc={pdfHtml}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </ModalBody>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              const w = window.open("", "_blank")
              if (w) {
                w.document.write(pdfHtml)
                w.document.close()
                setTimeout(() => w.print(), 500)
              }
            }}
          >
            <i className="mdi mdi-printer me-1" />
            Imprimir
          </Button>
          <Button color="secondary" size="sm" outline onClick={() => setPdfModal(false)}>
            Cerrar
          </Button>
        </div>
      </Modal>
    </>
  )
}

ExportarBoton.propTypes = {
  exportOpts: PropTypes.object.isRequired,
  sucursal: PropTypes.string,
  size: PropTypes.string,
  labelPDF: PropTypes.string,
  labelExcel: PropTypes.string,
  iconOnly: PropTypes.bool,
}

export default ExportarBoton
